import { api, ApiError } from '../lib/api.js';
import { applyBranding, esc, type Branding } from '../lib/brand.js';
import { centered, loading } from '../lib/shell.js';
import { icon } from '../lib/icons.js';
import {
  renderSections, renderWhyItems, visibleSections, SCALAR_TYPES,
  type FormSpec, type SpecSection,
} from '../lib/form-render.js';

interface FormConfig {
  form: { token: string; title: string };
  companyName: string;
  agencyName: string;
  branding: Branding;
  spec: FormSpec;
  consentText: string;
}

export async function renderPublicForm(root: HTMLElement, token: string): Promise<void> {
  root.innerHTML = loading();
  let cfg: FormConfig;
  try {
    cfg = await api.get<FormConfig>(`/api/public/${encodeURIComponent(token)}`);
  } catch (err) {
    const msg = err instanceof ApiError && err.status === 404
      ? 'This form is not available. It may be closed or the link is incorrect.'
      : 'Something went wrong loading this form.';
    root.innerHTML = centered(msg);
    return;
  }

  applyBranding(cfg.branding);
  const b = cfg.branding || {};
  const sections = visibleSections(cfg.spec);
  const ctx = { agencyName: cfg.agencyName, companyName: cfg.companyName, branding: b, consentText: cfg.consentText };
  const logoMark = b.logo_url
    ? `<img class="logo-img" src="${esc(b.logo_url)}" alt="${esc(cfg.agencyName)} logo" />`
    : `<span class="mark accent">${esc(initials(cfg.agencyName))}</span>`;

  root.innerHTML = `
  <div class="pub">
    <div class="pub-head">
      <div class="top">
        <div class="logo">${logoMark}
          <span class="name">${esc(cfg.agencyName)}</span></div>
        <div style="text-align:right">
          <div class="agency-tag">${esc(cfg.agencyName)}</div>
          ${b.support_phone ? `<div class="muted" style="font-size:13px">${esc(b.support_phone)}</div>` : ''}
        </div>
      </div>
      <div class="pub-banner">
        <h1>Employee Benefits Interest Form</h1>
        <p>Prepared for ${esc(cfg.companyName)} · Rated Top For Most Affordable Workplace Benefits</p>
      </div>
    </div>

    <div class="pub-grid">
      <form id="benefitForm" class="card"><div class="card-body">
        ${renderSections(cfg.spec, ctx)}
        <input type="text" id="hp_url" name="hp_url" tabindex="-1" autocomplete="off" aria-hidden="true" style="position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;opacity:0" />
        <div id="formErr" class="note" style="display:none;margin-top:16px;background:var(--danger-bg);border-color:#f5c6c2;color:#a3261c"></div>
        <button class="btn block" type="submit" style="margin-top:18px">Submit my interest form</button>
        <p class="muted" style="font-size:12px;text-align:center;margin:14px 0 0">
          Your information is sent only to ${esc(cfg.agencyName)}.${b.footer_text ? ' ' + esc(b.footer_text) + '.' : ''}
        </p>
      </div></form>

      <aside class="why"><h3>Why Choose Us</h3><ul>${renderWhyItems(cfg.spec)}</ul></aside>
    </div>
  </div>`;

  wireSubmit(root, cfg, sections, token);
}

function wireSubmit(root: HTMLElement, cfg: FormConfig, sections: SpecSection[], token: string): void {
  const form = root.querySelector('#benefitForm') as HTMLFormElement;
  const fields = sections.flatMap((s) => s.fields);
  const consentField = fields.find((f) => f.type === 'consent');

  form.onsubmit = async (e) => {
    e.preventDefault();
    const errBox = root.querySelector('#formErr') as HTMLElement;
    const payload: Record<string, unknown> = {};
    // Custom (agency/per-form) field answers travel under `custom`; the server
    // reads system fields top-level and custom fields from here (-> submissions.extra).
    const custom: Record<string, unknown> = {};

    for (const f of fields) {
      if (!SCALAR_TYPES.includes(f.type) || f.locked) continue;
      const el = root.querySelector('#' + f.key) as HTMLInputElement | null;
      const v = (el?.value ?? '').trim();
      if (f.required && !v) { showErr(errBox, `Please enter your ${f.label.toLowerCase()}.`); return; }
      if (f.type === 'tel' && v && !isValidPhone(v)) { showErr(errBox, 'Please enter a valid 10-digit phone number.'); return; }
      if (f.type === 'email' && v && !isValidEmail(v)) { showErr(errBox, 'Please enter a valid email address.'); return; }
      if (f.binding === 'custom') { if (v) custom[f.key] = v; }
      else payload[f.key] = v || null;
    }

    // Checkbox groups: built-in family_status -> object, coverage -> array (both
    // top-level, system columns); any custom group -> custom[key] as an array.
    const groups: Record<string, string[]> = {};
    root.querySelectorAll<HTMLInputElement>('input[data-group]:checked')
      .forEach((i) => { (groups[i.dataset.group!] ||= []).push(i.dataset.key!); });
    const family_status: Record<string, boolean> = {};
    (groups['family_status'] ?? []).forEach((k) => (family_status[k] = true));
    payload.family_status = family_status;
    payload.coverage = groups['coverage'] ?? [];
    for (const [gkey, vals] of Object.entries(groups)) {
      if (gkey !== 'family_status' && gkey !== 'coverage') custom[gkey] = vals;
    }
    payload.custom = custom;

    const consent = (root.querySelector('#consent') as HTMLInputElement | null)?.checked ?? false;
    if (consentField?.required && !consent) { showErr(errBox, 'Please accept the consent statement to continue.'); return; }
    payload.consent = consent;
    payload.hp_url = (root.querySelector('#hp_url') as HTMLInputElement).value;

    const submitBtn = form.querySelector('button[type=submit]') as HTMLButtonElement;
    submitBtn.disabled = true; submitBtn.textContent = 'Submitting…';
    try {
      await api.post(`/api/public/${encodeURIComponent(token)}`, payload);
      renderThanks(root, cfg);
    } catch (err) {
      submitBtn.disabled = false; submitBtn.textContent = 'Submit my interest form';
      showErr(errBox, err instanceof ApiError ? err.message : 'Submission failed. Please try again.');
    }
  };
}

function showErr(box: HTMLElement, msg: string) {
  box.textContent = msg;
  box.style.display = 'block';
}

/** Format guards; mirrored server-side in src/forms/spec.ts. */
function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}
function isValidPhone(v: string): boolean {
  const d = v.replace(/\D/g, '');
  return d.length === 10 || (d.length === 11 && d.startsWith('1'));
}

function renderThanks(root: HTMLElement, cfg: FormConfig) {
  const b = cfg.branding || {};
  root.innerHTML = `
  <div class="thanks">
    <div class="check-ring">${icon('check')}</div>
    <h1>Thank you!</h1>
    <p class="muted" style="font-size:16px">
      Your benefits interest form has been submitted to <strong>${esc(cfg.agencyName)}</strong>.
      A licensed agent will reach out shortly to walk you through your options.
    </p>
    <p class="muted" style="font-size:13px;margin-top:30px">${esc(b.footer_text || cfg.agencyName)}${b.support_phone ? ' · ' + esc(b.support_phone) : ''}</p>
  </div>`;
}

function initials(name: string): string {
  const p = name.trim().split(/\s+/);
  return ((p[0]?.[0] || '') + (p[1]?.[0] || '')).toUpperCase() || '?';
}
